"use client";

import Script from "next/script";
import { type VenueAdsConversionEntry } from "@punchup/analytics";
import {
  buildGa4Snippet,
  buildGtmSnippet,
  buildMetaPixelSnippet,
  buildTikTokPixelSnippet,
} from "../../analyticsLoader/snippets";
import type { VenueLoaderTag } from "../../analyticsLoader/types";
import PublishVenueAdsConversions from "./PublishVenueAdsConversions";
import VenueAnalyticsRoutePageView from "./VenueAnalyticsRoutePageView";

// Renders a venue's analytics tags: GTM first, then one shared gtag
// bootstrap for GA4 + Google Ads, then Meta Pixel, then TikTok Pixel — all
// `strategy="afterInteractive"`. Byte-parity with no google_ads rows is
// proven by the golden-string test in
// __tests__/VenueAnalyticsScripts.test.tsx; do not regenerate those strings.
//
// The first tag of each provider keeps the legacy DOM id
// (`gtm-venue-<id>` / `ga4-venue-<id>` / `meta-pixel-venue-<id>`); additional
// same-provider tags get an index suffix so ids stay unique.
const VenueAnalyticsScripts: React.FC<{
  venuePageId: string;
  tags: VenueLoaderTag[];
  googleAdsIds?: string[];
  conversions?: VenueAdsConversionEntry[];
}> = ({ venuePageId, tags, googleAdsIds = [], conversions = [] }) => {
  const gtmTags = tags.filter((tag) => tag.provider === "gtm");
  const ga4Tags = tags.filter((tag) => tag.provider === "ga4");
  const metaPixelTags = tags.filter((tag) => tag.provider === "meta_pixel");
  const tiktokPixelTags = tags.filter((tag) => tag.provider === "tiktok_pixel");

  if (
    gtmTags.length === 0 &&
    ga4Tags.length === 0 &&
    metaPixelTags.length === 0 &&
    tiktokPixelTags.length === 0 &&
    googleAdsIds.length === 0
  ) {
    return null;
  }

  return (
    <>
      {gtmTags.map((tag, index) => (
        <Script
          key={`gtm-${tag.measurementId}`}
          id={`gtm-venue-${venuePageId}${index > 0 ? `-${index}` : ""}`}
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: buildGtmSnippet(tag.measurementId),
          }}
        />
      ))}
      {(ga4Tags.length > 0 || googleAdsIds.length > 0) && (
        <>
          {/* Defense-in-depth behind the loader's render guards —
              encodeURIComponent is the identity function on every legal ID,
              so this never changes the golden-string output below. */}
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4Tags[0]?.measurementId ?? googleAdsIds[0])}`}
            strategy="afterInteractive"
          />
          <Script
            id={`ga4-venue-${venuePageId}`}
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html:
                buildGa4Snippet(
                  ga4Tags.map((tag) => ({
                    id: tag.measurementId,
                    sendPageView: tag.sendPageView,
                    linkerDomains: tag.linkerDomains,
                  }))
                ) +
                googleAdsIds.map((id) => `gtag('config', '${id}');\n`).join(""),
            }}
          />
        </>
      )}
      {metaPixelTags.map((tag, index) => (
        <Script
          key={`meta-pixel-${tag.measurementId}`}
          id={`meta-pixel-venue-${venuePageId}${index > 0 ? `-${index}` : ""}`}
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: buildMetaPixelSnippet(tag.measurementId),
          }}
        />
      ))}
      {tiktokPixelTags.map((tag, index) => (
        <Script
          key={`tiktok-pixel-${tag.measurementId}`}
          id={`tiktok-pixel-venue-${venuePageId}${index > 0 ? `-${index}` : ""}`}
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: buildTikTokPixelSnippet(tag.measurementId),
          }}
        />
      ))}
      {(metaPixelTags.length > 0 || tiktokPixelTags.length > 0) && (
        <VenueAnalyticsRoutePageView
          metaPixelIds={metaPixelTags.map((tag) => tag.measurementId)}
          tiktokPixelIds={tiktokPixelTags.map((tag) => tag.measurementId)}
        />
      )}
      {conversions.length > 0 && (
        <PublishVenueAdsConversions entries={conversions} />
      )}
    </>
  );
};

export default VenueAnalyticsScripts;
